import React, { useState, useEffect, useRef, useCallback } from "react";
import cn from "classnames";
import { FormattedMessage } from "react-intl";

import { Wrapper, StyledSelect, Options, Option, SelectWrapper } from "../../Select/InputSelect/styles";
import Checkbox from "../../Checkbox";

import messages from "../../Select/messages";
import { IOption, IProps } from "./interface";

const InputSelectCheckbox = <T extends string | number>({
    value, onChange, disabled, name, options, lightWithValue, join, simulation,
}: IProps<T>) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isFlipped, setIsFlipped] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const clickOutside = useCallback((e: MouseEvent) => {
        if (
            wrapperRef.current! !== (e.target) &&
            !wrapperRef.current!.contains(e.target as Node) &&
            !simulation
        ) {
            setIsOpen(false);
            window.removeEventListener("click", clickOutside);
        }
    }, [wrapperRef, simulation, setIsOpen]);
    const handleOpen = () => {
        if (simulation) return;
        let { top } = wrapperRef!.current!.getBoundingClientRect();
        let { innerHeight } = window;
        let flipped = false;
        if (top + 21 > innerHeight / 2) {
            flipped = true;
        }
        if (disabled) return;
        if (!isOpen) {
            setTimeout(() => {
                window.addEventListener("click", clickOutside);
            });
        } else {
            window.removeEventListener("click", clickOutside);
        }
        setIsOpen(old => !old);
        setIsFlipped(flipped);
    };

    
    useEffect(() => {
        if (simulation) {
            setIsOpen(true);
        }
        return () => {
            window.removeEventListener("click", clickOutside);
        }
    }, []);

    let hasValue = value !== undefined && value.length > 0;

    return (
        <Wrapper ref={wrapperRef} style={{ marginBottom: simulation ? 270 : 0 }}>
            <SelectWrapper
                className={cn(
                    disabled && "SelectDisabled",
                    isOpen && "SelectOpened",
                    lightWithValue && value && value.length > 0 && "SelectLight"
                )}
            >
                <StyledSelect tabIndex={0} onClick={handleOpen}>
                    {hasValue ? (
                        options
                            .filter((o) => {
                                if (value!.indexOf(o.value) !== -1) {
                                    return true;
                                }
                                return false;
                            })
                            .map((o, index) => (
                                <span key={o.value}>
                                    {o.label}
                                    {index < options.filter((o2) => {
                                        if (value!.indexOf(o2.value) !== -1) {
                                            return true;
                                        }
                                        return false;
                                    }).length - 1 && join}
                                </span>
                            ))
                    ) : (
                        <FormattedMessage {...messages.choose} />
                    )}
                    <div className={"Caret"}>
                        <svg width="11" height="6">
                            <polygon points="0,0 11,0 5.5,5.5" />
                        </svg>
                    </div>
                </StyledSelect>
            </SelectWrapper>
            {isOpen && (
                <Options className={cn(isFlipped && "Flipped")}>
                    <div className={"OptionsScroll"}>
                        {hasValue && (
                            <Option
                                onClick={() => {
                                    onChange({
                                        target: {
                                            name,
                                            value: undefined,
                                            type: "select-checkbox",
                                        },
                                    });
                                }}
                            >
                                <FormattedMessage {...messages.delete} />
                            </Option>
                        )}
                        {options.map((option) => {
                            return (
                                <Checkbox
                                    key={option.value}
                                    label={option.label}
                                    checked={!!value && value.indexOf(option.value) !== -1}
                                    onChange={() => {
                                        onChange({
                                            target: {
                                                name,
                                                value: option.value,
                                                type: "select-checkbox",
                                            },
                                        });
                                    }}
                                    select
                                ></Checkbox>
                            );
                        })}
                    </div>
                </Options>
            )}
        </Wrapper>
    );
}

export default InputSelectCheckbox;
